import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from '@app/store';
import {
  createAssetType,
  updateAssetType,
  fetchAssetTypeById,
  assetTypeActions,
} from "./assetTypeSlice";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { Input } from "@/components/ui/input";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  SwapVerticalCircle as MenuIcon,
} from "@mui/icons-material";
import {
  AssetTypeFieldType,
  AssetTypeFieldTypeLabels,
  AssetTypeFieldData,
  AssetTypeOutputDTO,
  AssetTypeType,
  AssetTypeTypeLabels,
} from './types';
import { ApiError } from '@/types/api';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { assetTypeMapper } from "./assetTypeMapper";
import { useNotification } from '@hooks/useNotification';
import { SelectField } from '@features/CompleteForm/components/SelectField';

import styled from "styled-components";
import { DeleteButton } from "@components/ui/DeleteButton";

const DropZone = styled.div`
  height: ${(props) => (props.hidden ? "0px" : "20px")};
  ${(props) => (props.hidden ? "padding: 0px;" : "")};
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%; /* Ocupa toda a largura disponível */
`;

const LinhaSuperior = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const BoxContainer = styled.div`
  flex-shrink: 0; /* Não deixa o Box crescer */
  margin-right: 8px; /* Espaçamento entre os itens */
`;

const FormContainer = styled.div`
  flex-grow: 1; /* Ocupa todo o espaço restante */
`;

const DropZoneContainer = styled.div`
  width: 100%; /* Ocupa toda a largura */
`;

const AssetTypeForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.assetType
  );
  const [formData, setFormData] = useState<Partial<AssetTypeOutputDTO>>({});
  const [localFields, setLocalFields] = useState<AssetTypeFieldData[]>([]);
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  const [dragged, setDragged] = useState<number | null>(null); // storing the dragged item as an index
  const [mouse, setMouse] = useState<[number, number]>([0, 0]);
  const [dropZone, setDropZone] = useState(0);

  // drop item
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dragged !== null) {
        e.preventDefault();
        setDragged(null);

        if (
          dragged < 0 ||
          dragged >= localFields.length ||
          dropZone < 0 ||
          dropZone >= localFields.length
        ) {
          console.error("Índices inválidos.");
          return;
        }

        const novolocalFields = [...localFields]; // Cria uma cópia para evitar mutação
        const [itemRemovido] = novolocalFields.splice(dragged, 1); // Remove o item da posição B
        novolocalFields.splice(
          dropZone + (dragged <= dropZone ? 0 : 1),
          0,
          itemRemovido
        ); // Insere o item na posição C

        setLocalFields(novolocalFields);
      }
    };

    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  });

  // get closest drop zone
  useEffect(() => {
    if (dragged !== null) {
      // get all drop-zones
      const elements = Array.from(document.getElementsByClassName("drop-zone"));
      // get all drop-zones' y-axis position
      const positions = elements.map((e) => [
        e.id,
        e.getBoundingClientRect().top,
      ]);
      // get the difference with the mouse's y position
      const absDifferences = positions.map((v: any) =>
        Math.abs(v[1] - mouse[1])
      );

      // get the item closest to the mouse
      let result = absDifferences.indexOf(Math.min(...absDifferences));
      result += dragged <= result ? 1 : 0;
      console.log(mouse, dragged, result, positions);
      setDropZone(result);
    }
  }, [dragged, mouse]);

  // get mouse coordinates
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      setMouse([e.x, e.y]);
    };

    document.addEventListener("mousemove", handler);

    return () => document.removeEventListener("mousemove", handler);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do AssetType:", id);
        isLoadingRef.current = true;

        if (id) {
          console.log("📡 PhaseFormForm - Carregando AssetType:", id);
          const resultAction = await dispatch(fetchAssetTypeById(id));

          if (fetchAssetTypeById.fulfilled.match(resultAction)) {
            console.log("✅ AssetType carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar AssetType:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(assetTypeActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 AssetType - Atualizando dados do formulário:",
        currentItem
      );

      setFormData({
        name: currentItem.name,
        description: currentItem.description,
        type: currentItem.type,
      });

      if (currentItem.data) {
        setLocalFields(currentItem.data);
      }
    }
  }, [currentItem]);

  const fields: Field[] = [
     {
      name:'section1',
      type:'section',
      label:'Informações'
    },
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "description",
      label: "Descrição",
      type: "textarea",
      rows: 4,
      validationError: formError?.validationErrors?.description?.message,
    },
    {
      name: "type",
      label: "Tipo",
      type: "select",
      options: [
        {
          value: AssetTypeType.MOVABLE,
          label: AssetTypeTypeLabels[AssetTypeType.MOVABLE],
        },
        {
          value: AssetTypeType.IMMOVABLE,
          label: AssetTypeTypeLabels[AssetTypeType.IMMOVABLE],
        },
      ],
      required: true,
      validationError: formError?.validationErrors?.type?.message,
    },
  ];

  const handleFieldUpdate = (
    index: number,
    updatedField: AssetTypeFieldData
  ) => {
    console.log("Updating field at index:", index, updatedField); // Debug
    const newFields = [...localFields];
    newFields[index] = updatedField;
    setLocalFields(newFields);
  };

  const handleSubmit = async (data: Partial<AssetTypeOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      const finalData = {
        ...data,
        data: localFields,
      };

      const mappedData = assetTypeMapper.toDTO(finalData);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateAssetType({ id, data: mappedData }))
        : await dispatch(createAssetType(mappedData));

      if (
        updateAssetType.rejected.match(resultAction) ||
        createAssetType.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/asset-types");
    } catch (error) {
      console.error("❌ AssetTypeForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  const handleAddField = () => {
    setLocalFields((prev) => [
      ...prev,
      {
        _id: "",
        name: "",
        type: AssetTypeFieldType.STRING,
      },
    ]);
  };

  const handleFieldRemove = (index: number) => {
    setLocalFields((prev) => prev.filter((_, i) => i !== index));
  };

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (isInitialLoad && loading) {
    return <FormSkeleton fields={2} additionalFields={true} />;
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* <Typography variant="h5" gutterBottom>
        {id ? "Editar Tipo de Bem" : "Novo Tipo de Bem"}
      </Typography> */}
      <DataForm
        data={formData}
        fields={fields}
        onSubmit={handleSubmit}
        basePath="asset-types"
        title={id ? "Editar Tipo de Bem" : "Novo Tipo de Bem"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
        children={()=><Box sx={{ mt: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <p style={{ fontSize: 18 }}><b>Campos Personalizados</b></p>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              handleAddField();
              setExpandedIndex(localFields.length); // Expand the newly added field
              setTimeout(() => {
                window.scrollTo({
                  top: document.body.scrollHeight,
                  behavior: "smooth"
                });
              }, 150);
            }}
            variant="outlined"
            size="small"
          >
            Adicionar Campo
          </Button>
        </Box>
        {localFields.length==0 && 
          <p style={{textAlign:'center'}}>Nenhum campo adicionado</p>
        }

        {localFields.map((field, index) => (
          <Box key={index}>
            {dragged === index  && (
              <div
                style={{
                  left: `${mouse[0]}px`,
                  top: `${mouse[1]}px`,
                  position: "fixed",
                  boxShadow: "5px 5px 10px rgba(0,0,0,0.5)",
                }}
              >
                <Box>{localFields[dragged].name}</Box>
              </div>
            )}
            {dragged !== index && (
              <Container>
                <LinhaSuperior>
                  <FormContainer>
                    <DataItemForm
                      field={field}
                      index={index}
                      id={"field-" + index}
                      onUpdate={handleFieldUpdate}
                      onRemove={handleFieldRemove}
                      error={formError}
                      setDragged={setDragged}
                      expanded={expandedIndex === index}
                      setExpandedIndex={setExpandedIndex}
                    />
                  </FormContainer>
                </LinhaSuperior>
                <DropZoneContainer className="drop-zone">
                  <DropZone
                    hidden={dragged === null || dropZone !== index}
                    color="red"
                  ></DropZone>
                </DropZoneContainer>
              </Container>
            )}
          </Box>
        ))}
      </Box>}
      />

      
    </Box>
  );
};

interface DataItemFormProps {
  field: AssetTypeFieldData;
  index: number;
  id: string;
  onUpdate: (index: number, data: AssetTypeFieldData) => void;
  onRemove: (index: number) => void;
  error?: any;
  setDragged?: any;
  expanded: boolean;
  setExpandedIndex: (index: number | null) => void;
}

const DataItemForm: React.FC<DataItemFormProps> = ({
  field,
  index,
  id,
  onUpdate,
  onRemove,
  error,
  setDragged,
  expanded,
  setExpandedIndex,
}) => {
  const handleExpandChange = () => {
    setExpandedIndex(expanded ? null : index);
  };

  const handleFieldChange = (name: string, value: any) => {
    onUpdate(index, {
      ...field,
      [name]: value,
    });
  };

  const handleTypeChange = (newType: AssetTypeFieldType) => {
    console.log("handleTypeChange:", newType);
    onUpdate(index, {
      ...field,
      type: newType,
      options: newType === AssetTypeFieldType.SELECT ? [] : undefined,
    });
  };

  const handleAddOption = () => {
    const newOptions = [...(field.options || []), ""];
    handleFieldChange("options", newOptions);
  };

  const handleOptionChange = (optionIndex: number, value: string) => {
    const newOptions = [...(field.options || [])];
    newOptions[optionIndex] = value;
    handleFieldChange("options", newOptions);
  };

  const handleRemoveOption = (optionIndex: number) => {
    const newOptions = (field.options || []).filter((_, i) => i !== optionIndex);
    handleFieldChange("options", newOptions);
  };

  const baseFields: Field[] = [
    {
      name: "name",
      label: "Nome do Campo",
      type: "text",
      required: true,
    },
    {
      name: "type",
      label: "Tipo do Campo",
      type: "select",
      required: true,
      options: Object.entries(AssetTypeFieldTypeLabels).map(
        ([value, label]) => ({
          value: value.toUpperCase(),
          label,
        })
      ),
      value: field.type,
      onChange: (value) => handleTypeChange(value as AssetTypeFieldType),
    },
    {
      name: "required",
      label: "Obrigatório?",
      type: "checkbox",
      required: false,
      value: field.required,
    },
  ];

  return (
    <Accordion id={id} expanded={expanded} onChange={handleExpandChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <BoxContainer>
          <Box
            key={"key" + index}
            onMouseDown={(e) => {
              e.preventDefault();
              setDragged(index);
            }}
          >
            <MenuIcon />
          </Box>
        </BoxContainer>

        <Typography>{field.name || "Campo sem nome"}</Typography>

        <DeleteButton
          sx={{ marginRight: 2 }}
          messageText="Confirma a remoção do item?"
          onRemove={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
        />
      </AccordionSummary>
      <AccordionDetails>
        <DataForm
          data={field}
          fields={baseFields}
          onSubmit={async () => Promise.resolve()}
          basePath=""
          title=""
          hideButtons
          onChange={(name, value) => handleFieldChange(name, value)}
        />

        {field.type === AssetTypeFieldType.SELECT && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Opções de Seleção
            </Typography>
            {(field.options || []).map((option, optionIndex) => (
              <Box
                key={optionIndex}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box sx={{ flexGrow: 1 }}>
                  <DataForm
                    data={{ value: option }}
                    fields={[
                      {
                        name: "value",
                        label: `Opção ${optionIndex + 1}`,
                        type: "text",
                        required: true,
                      },
                    ]}
                    onSubmit={async () => Promise.resolve()}
                    basePath=""
                    title=""
                    hideButtons
                    onChange={(name, value) => handleOptionChange(optionIndex, value)}
                  />
                </Box>
                <DeleteButton
                  messageText="Confirma a remoção da opção?"
                  onRemove={() => handleRemoveOption(optionIndex)}
                />
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddOption}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              Adicionar Opção
            </Button>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AssetTypeForm;
